import { useUtilityServices } from "@features/bx/bulk-bill-generation/hooks";
import { QuickStatsCard } from "@features/cx/shared/components/QuickStatsCard";
import { queryClient } from "@shared/api/queries/queryClient";
import { QueryKeyFactory } from "@shared/api/queries/queryKeyFactory";
import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Input } from "@shared/ui/input";
import {
  Check,
  ChevronDown,
  Edit,
  FileText,
  Loader2,
  Save,
  X,
} from "lucide-react";
import { useMemo, useState } from "react";
import { GenericTable } from "../../../../../shared/components/GenericTable";
import { useBillDetails, useSummary, useUpdateGlCode } from "../../hooks"; // Adjust import path as needed
import { logEvent } from "@shared/analytics/analytics";

// Custom Switch component for status toggle
const StatusSwitch = ({ checked, onToggle, disabled = false }) => {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`
        relative inline-flex h-6 w-11 items-center rounded-full transition-colors
        ${checked ? "bg-blue-600" : "bg-gray-200"}
        ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
      `}
    >
      <span
        className={`
          inline-block h-5 w-5 transform rounded-full bg-white transition-transform
          ${checked ? "translate-x-5" : "translate-x-0.5"}
        `}
      />
    </button>
  );
};

// Custom Select component
const CustomSelect = ({
  value,
  onValueChange,
  options,
  placeholder,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="relative">
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          inline-flex items-center justify-between px-4 py-2 bg-white border border-gray-300 
          rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-32
          ${disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}
        `}
      >
        <span>{value || placeholder}</span>
        <ChevronDown className="w-4 h-4 ml-2" />
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => {
                onValueChange(option.value);
                setIsOpen(false);
              }}
              className="w-full px-4 py-2 text-left hover:bg-gray-100 cursor-pointer"
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const GLEntityDashboard = () => {
  // State management
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [utilityFilter, setUtilityFilter] = useState("All Utilities");
  const [selectedRows, setSelectedRows] = useState([]);
  const [editingGLCode, setEditingGLCode] = useState(null); // Track which GL code is being edited
  const [editValue, setEditValue] = useState(""); // Track the edit input value
  const [savingGLCode, setSavingGLCode] = useState(null); // Track which GL code is being saved

  // Query parameters for API
  const queryParams = useMemo(
    () => ({
      search_data: searchTerm || undefined,
      status: statusFilter !== "All Status" ? statusFilter : undefined,
      product_code:
        utilityFilter !== "All Utilities" ? utilityFilter : undefined,
    }),
    [searchTerm, statusFilter, utilityFilter]
  );

  const { data: glCodeList, isLoading } = useBillDetails(queryParams);
  const updateGlCode = useUpdateGlCode();

  const handleToggleStatus = async (item) => {
    item["isActiveStatus"] = !item["isActiveStatus"];
    console.log(!item["isActiveStatus"]);
    updateGlCode.mutate(
      { entityName: item.entityName, item: item },
      {
        onSuccess: async () => {
          logEvent("GL Code Status Toggled", {
            active: !item["isActiveStatus"],
          });
          await queryClient.invalidateQueries({
            queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
          });
          await queryClient.invalidateQueries({
            queryKey: [QueryKeyFactory.module.bx.glCode.summary()],
          });
        },
      }
    );
  };

  const handleEditGLCode = (row) => {
    setEditingGLCode(row.id);
    setEditValue(row.configuredGlCodeValue || "");
  };

  const handleSaveGLCode = async (item) => {
    item["configuredGlCodeValue"] = editValue;
    updateGlCode.mutate(
      { entityName: item.entityName, item: item },
      {
        onSuccess: async () => {
          handleCancelEdit();
          logEvent(`BX GL Code Edited`, {
            entityName: item.entityName,
            item: item,
          });
          await queryClient.invalidateQueries({
            queryKey: [QueryKeyFactory.module.bx.glCode.glCodeList()],
          });
        },
      }
    );
  };

  const { data: summary } = useSummary();

  // KPI Cards configuration using QuickStatsCard
  const glEntityKpiCards = [
    {
      title: "Total Entities",
      count: summary?.total || 0,
      trendSummary: "Total GL entities in the system",
      trendDirection: "up" as const,
      icon: <FileText className="h-8 w-8 text-gray-500" />,
    },
    {
      title: "Active Entities",
      count: summary?.active || 0,
      trendSummary: "Entities currently enabled and operational",
      trendDirection: "up" as const,
      icon: <Check className="h-8 w-8 text-green-500" />,
    },
    {
      title: "Inactive Entities",
      count: summary?.inActive || 0,
      trendSummary: "Entities currently disabled",
      trendDirection: "down" as const,
      icon: <X className="h-8 w-8 text-red-500" />,
    },
    {
      title: "Configured with GL Codes",
      count: summary?.configuredWithGlCode || 0,
      trendSummary: "Entities with assigned GL codes",
      trendDirection: "up" as const,
      icon: <FileText className="h-8 w-8 text-blue-500" />,
    },
  ];

  const handleCancelEdit = () => {
    setEditingGLCode(null);
    setEditValue("");
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("All Status");
    setUtilityFilter("All Utilities");
  };

  const { data: utilityServiceList } = useUtilityServices();

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const columns = [
    {
      key: "category",
      header: "Category",
      type: "badge",
      badgeConfig: {
        getVariant: () => "default",
        getClassName: (value) => {
          const categoryColors = {
            Domestic: "bg-blue-100 text-blue-800",
            Industrial: "bg-purple-100 text-purple-800",
            Commercial: "bg-green-100 text-green-800",
          };
          return categoryColors[value] || "bg-gray-100 text-gray-800";
        },
      },
    },
    {
      key: "entityName",
      header: "Entity Name",
      render: (value) => (
        <span className="text-sm font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "productCode",
      header: "Utility",
      render: (value) => <span className="text-sm text-gray-500">{value}</span>,
    },
    {
      key: "configuredGlCodeValue",
      header: "GL Code",
      render: (value, row) => {
        // Check if this row is being edited
        if (editingGLCode === row.id) {
          return (
            <div className="flex items-center gap-2">
              <Input
                value={editValue}
                onChange={(e) => setEditValue(e.target.value)}
                placeholder="Enter GL Code"
                className="w-32 h-8"
                autoFocus
              />
              <button
                onClick={() => handleSaveGLCode(row)}
                disabled={savingGLCode === row.id || !editValue.trim()}
                className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
                title="Save"
              >
                {savingGLCode === row.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={savingGLCode === row.id}
                className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
                title="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        }

        // Normal display mode
        return (
          <span className="text-sm text-gray-500">
            {value ? (
              <span className="font-mono">{value}</span>
            ) : (
              <span className="italic text-gray-400">Not configured</span>
            )}
          </span>
        );
      },
    },
    {
      key: "isActiveStatus",
      header: "Status",
      type: "badge",
      render: (value) => {
        const isActive = value === true || value === "true"; // handles both boolean and string "true"
        const className = isActive
          ? "bg-green-100 text-green-800 px-2 py-1 rounded text-sm font-medium"
          : "bg-purple-100 text-gray-800 px-2 py-1 rounded text-sm font-medium";

        return (
          <span className={className}>{isActive ? "Active" : "Inactive"}</span>
        );
      },
    },

    {
      key: "toggle",
      header: "Actions",
      render: (value, row) => (
        <div className="flex gap-2">
          {editingGLCode !== row.id && (
            <button
              onClick={() => handleEditGLCode(row)}
              className="p-1 text-blue-600 hover:text-blue-700"
              title="Edit GL Code"
            >
              <Edit className="h-4 w-4" />
            </button>
          )}
          <StatusSwitch
            checked={row.isActiveStatus}
            onToggle={() => handleToggleStatus(row)}
            disabled={editingGLCode === row.id}
          />
        </div>
      ),
    },
  ];

  const handleSubmit = (data: any) => {
    console.log("Filter form data:", data);
    setStatusFilter(data.statusFilter || "All Status");
    setUtilityFilter(data.utilityFilter || "All Utilities");
  };

  const handleReset = () => {
    setStatusFilter("All Status");
    setUtilityFilter("All Utilities");
    form.reset({
      statusFilter: "All Status",
      utilityFilter: [],
    });
  };
  // Filter options
  const statusOptions = [
    { value: "Active", label: "Active" },
    { value: "Inactive", label: "Inactive" },
  ];

  const formFields: FormField[] = useMemo(
    () => [
      {
        name: "statusFilter",
        type: "select",
        label: "Status",
        placeholder: "All Status",
        disabled: isLoading,
        options: statusOptions,
      },
      {
        name: "utilityFilter",
        type: "multi-select",
        label: "Utilities",
        placeholder: "All Utilities",
        disabled: isLoading,
        searchable: true,
        clearable: true,
        maxDisplayItems: 2,
        options:
          utilityServiceList?.map((service) => ({
            value: service.id,
            label: service.name,
          })) || [],
      },
    ],
    [isLoading, statusOptions, utilityServiceList]
  );

  const form = FormService.createForm({
    fields: formFields, // Will be defined below
    mode: "onChange",
  });

  // Inline filters component
  const InlineFilters = () => (
    <DynamicForm
      fields={formFields}
      form={form}
      onSubmit={handleSubmit}
      onReset={handleReset}
      config={{
        showSubmitButton: true,
        showResetButton: true,
        submitButtonText: "Apply Filters",
        resetButtonText: "Clear Filters",
        classes: {
          buttonContainer: 'justify-end'
        }
      }}
      gridColumns={2}
      className="w-full"
    />
  );

  return (
    <div className="">
      <div>
        <div className="pb-4">
          <h2 className="page-title">GL Codes</h2>
        <p className="page-subtitle">
          Manage General Ledger (GL) codes for financial mapping and accounting
          integration.
        </p>
        </div>
      </div>

      {/* KPI Summary Cards - Using loop with QuickStatsCard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8 pt-2">
        {glEntityKpiCards.map((card, index) => (
          <QuickStatsCard
            key={index}
            title={card.title}
            count={card.count}
            trendSummary={card.trendSummary}
            trendDirection={card.trendDirection}
            icon={card.icon}
          />
        ))}
      </div>

      {/* Generic Table */}
      <GenericTable
        data={glCodeList?.glCodes || []}
        columns={columns}
        rowKey="id"
        actionsType="icons"
        emptyMessage="No GL entities found matching your search criteria."
        loading={isLoading}
        filterPanel={<InlineFilters />}
        onSearch={handleSearch}
        searchPlaceholder="Search entities..."
      />
    </div>
  );
};

export default GLEntityDashboard;
